import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { BookingStatus } from '@shared/types/booking';
import { formatCurrency } from '@lib/utils';
import { format } from 'date-fns';
import { ROUTES } from '@lib/routes';
import { useUserBookingsById, useCancelBooking } from '@hooks/useBookings';
import { useAuth } from '@hooks/useAuth';
import { getBike } from '@lib/firebase/bikes';
import { getLocation } from '@lib/firebase/locations';
import type { Bike } from '@shared/types/bike';
import type { Location } from '@shared/types/bike';

const statusColors = {
  [BookingStatus.PENDING]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  [BookingStatus.CONFIRMED]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  [BookingStatus.ACTIVE]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  [BookingStatus.COMPLETED]: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  [BookingStatus.CANCELLED]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function MyBookingsPage(): JSX.Element {
  const { user } = useAuth();
  const { data: bookings = [], isLoading } = useUserBookingsById(user?.id || '');
  const cancelBooking = useCancelBooking();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [bikeMap, setBikeMap] = useState<Record<string, Bike>>({});
  const [locationMap, setLocationMap] = useState<Record<string, Location>>({});

  // Load bike and location data for bookings
  useEffect(() => {
    const loadBookingDetails = async () => {
      if (!bookings.length) return;

      const bikeIds = [...new Set(bookings.map((b) => b.bikeId))];
      const locationIds = [...new Set(bookings.map((b) => b.locationId))];

      const bikes: Record<string, Bike> = {};
      const locations: Record<string, Location> = {};

      await Promise.all([
        ...bikeIds.map(async (id) => {
          const bike = await getBike(id);
          if (bike) bikes[id] = bike;
        }),
        ...locationIds.map(async (id) => {
          const location = await getLocation(id);
          if (location) locations[id] = location;
        }),
      ]);

      setBikeMap(bikes);
      setLocationMap(locations);
    };

    if (bookings.length > 0) {
      loadBookingDetails();
    }
  }, [bookings]);

  const handleCancel = async (bookingId: string) => {
    if (!confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setCancellingId(bookingId);
      await cancelBooking.mutateAsync({ bookingId, reason: 'Cancelled by user' });
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      alert('Failed to cancel booking. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  const upcomingBookings = bookings.filter(
    (booking) =>
      booking.status === BookingStatus.CONFIRMED ||
      booking.status === BookingStatus.ACTIVE ||
      booking.status === BookingStatus.PENDING
  );

  const pastBookings = bookings.filter(
    (booking) =>
      booking.status === BookingStatus.COMPLETED || booking.status === BookingStatus.CANCELLED
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-primary">My Bookings</h1>
          <Link to={ROUTES.CUSTOMER.SEARCH}>
            <Button>Book Another Bike</Button>
          </Link>
        </div>

        {/* Upcoming Bookings */}
        {upcomingBookings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-primary mb-4">
              Upcoming Bookings
            </h2>
            <div className="space-y-4">
              {upcomingBookings.map((booking) => {
                const bike = bikeMap[booking.bikeId];
                const location = locationMap[booking.locationId];
                return (
                  <Card key={booking.id} hover>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-primary">
                            {bike?.name || 'Bike'}
                          </h3>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${statusColors[booking.status]}`}
                          >
                            {booking.status}
                          </span>
                        </div>
                        {location && (
                          <p className="text-sm text-theme-subtext mb-1">
                            <strong>Location:</strong> {location.name}
                          </p>
                        )}
                        <p className="text-sm text-theme-subtext mb-1">
                          <strong>Start:</strong>{' '}
                          {format(booking.startTime, 'MMM d, yyyy h:mm a')}
                        </p>
                        <p className="text-sm text-theme-subtext mb-2">
                          <strong>End:</strong> {format(booking.endTime, 'MMM d, yyyy h:mm a')}
                        </p>
                        <p className="text-lg font-semibold text-primary">
                          {formatCurrency(booking.totalPrice)}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {booking.qrCode && (
                          <div className="text-center">
                            <img
                              src={booking.qrCode}
                              alt="QR Code"
                              className="w-32 h-32 mx-auto border border-gray-300 dark:border-gray-600 rounded"
                            />
                            <p className="text-xs text-theme-subtext mt-1">
                              Show at check-in
                            </p>
                          </div>
                        )}
                        {(booking.status === BookingStatus.CONFIRMED ||
                          booking.status === BookingStatus.PENDING) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancel(booking.id)}
                            isLoading={cancellingId === booking.id}
                          >
                            Cancel Booking
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-primary mb-4">
              Past Bookings
            </h2>
            <div className="space-y-4">
              {pastBookings.map((booking) => {
                const bike = bikeMap[booking.bikeId];
                return (
                  <Card key={booking.id}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-primary">
                            {bike?.name || 'Bike'}
                          </h3>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${statusColors[booking.status]}`}
                          >
                            {booking.status}
                          </span>
                        </div>
                        <p className="text-sm text-theme-subtext">
                          {format(booking.startTime, 'MMM d, yyyy')} -{' '}
                          {format(booking.endTime, 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-primary">
                          {formatCurrency(booking.totalPrice)}
                        </p>
                        {booking.status === BookingStatus.COMPLETED && bike && (
                          <Link to={ROUTES.CUSTOMER.BIKE_DETAIL.replace(':bikeId', bike.id)}>
                            <Button variant="ghost" size="sm" className="mt-2">
                              Book Again
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {bookings.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <p className="text-theme-subtext text-lg mb-4">
                You don't have any bookings yet
              </p>
              <Link to={ROUTES.CUSTOMER.SEARCH}>
                <Button>Search Hubs</Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

